import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { supabase } from "../config/supabaseClient";
import { Slider } from "./Slider";

const PageWrapper = styled.div`
  padding: 40px 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
`;

const ArticleContainer = styled.article`
  max-width: 80%;
  margin: 0 auto;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 2rem;
  margin-top: 8rem;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0rem;
    margin-top: 4rem;
    background-color: transparent;
    box-shadow: none;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1.5rem;
  font-family: "Poppins";
`;

const Content = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #444;
  margin-bottom: 2rem;

  h1, h2, h3, h4, h5, h6 {
    margin: 1.2em 0 0.6em;
    font-weight: 600;
  }

  p {
    margin: 0.7em 0;
  }

  ul, ol {
    padding-left: 1.5em;
    margin: 0.7em 0;
  }

  blockquote {
    border-left: 3px solid rgb(167, 140, 76);
    padding-left: 1em;
    margin-left: 0;
    font-style: italic;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  code {
    background-color: #f0f0f0;
    padding: 0.1em 0.3em;
    border-radius: 3px;
    font-family: monospace;
  }

  a, .article-link {
    color: rgb(167, 140, 76);
    text-decoration: underline;
    transition: color 0.2s;

    &:hover {
      color: #bc9f8b;
    }
  }
`;

const BackButton = styled(Link)`
  display: inline-block;
  background-color: rgb(167, 140, 76);
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 5px;
  text-decoration: none;
  margin-bottom: 2rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #bc9f8b;
  }
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid rgb(167, 140, 76);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 20px auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
`;

const SingleProject = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const { data, error } = await supabase
          .from("projects")
          .select("*")
          .eq("id", id)
          .single();

        if (error) throw error;
        if (data) setArticle(data);
      } catch (err) {
        console.error("Error fetching article:", err);
        setError("Failed to load project.");
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  if (loading) {
    return (
      <PageWrapper>
        <LoadingSpinner />
      </PageWrapper>
    );
  }

  if (error || !article) {
    return (
      <PageWrapper>
        <ErrorMessage>{error || "Project not found."}</ErrorMessage>
        <BackButton to="/projects">← Back to Projects</BackButton>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ArticleContainer>
        <BackButton to="/projects">← Back to Projects</BackButton>

        {article && (
          <>
            <Slider images={article.images || []} />
            <Title>{article.title}</Title>
            {
              article.sold ? <p style={{color: `#ff4d4f`}}><b>SOLD</b></p> : <p style={{color: `#52c41a`}}><b>AVAILABLE</b></p>
            }
            <p>{article.price} AED</p>
            <p>{article.area}</p>
            <p>
              <i
                className="fa-solid fa-location-dot"
                style={{ marginRight: "6px", color: "#c8c8c8" }}
              ></i>
              {article.location}
            </p>
            <Content dangerouslySetInnerHTML={{ __html: article.content || "" }} />
          </>
        )}
      </ArticleContainer>
    </PageWrapper>
  );
};

export default SingleProject;
